import React, { Component } from "react";
import Form from "react-validation/build/form";
import Input from "react-validation/build/input";
import CheckButton from "react-validation/build/button";
import ReCaptcha from "react-google-recaptcha";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { withTranslation } from "react-i18next";

import AuthService from "../services/auth.service";
import EmailService from "../services/email.service";

import { withRouter } from '../common/with-router';

const required = (t) => (value) => {
  if (!value) {
    return (
      <div className="alert alert-danger" role="alert">
        {t("requiredField")}
      </div>
    );
  }
};

const vsafe = (t) => (value) => {
  if (/[<>$]/.test(value)) {
    return (
      <div className="alert alert-danger" role="alert">
        {t("invalidCharacters")}
      </div>
    );
  }
};

class Login extends Component {
  constructor(props) {
    super(props);
    this.handleLogin = this.handleLogin.bind(this);
    this.onChangeUsername = this.onChangeUsername.bind(this);
    this.onChangePassword = this.onChangePassword.bind(this);
    this.handleForgotPassword = this.handleForgotPassword.bind(this);
    this.openForgotPasswordModal = this.openForgotPasswordModal.bind(this);
    this.closeForgotPasswordModal = this.closeForgotPasswordModal.bind(this);
    this.onChangeResetEmail = this.onChangeResetEmail.bind(this);
    this.sendPasswordReset = this.sendPasswordReset.bind(this);

    this.state = {
      username: "",
      password: "",
      showPassword: false,
      loading: false,
      message: "",
      recaptcha: null,
      successful: false,
      showForgotPasswordModal: false,
      resetEmail: "",
      resetLoading: false,
      resetMessage: "",
      resetSuccessful: false
    };
  }

  onChangeUsername(e) {
    this.setState({
      username: e.target.value
    });
  }

  onChangePassword(e) {
    this.setState({
      password: e.target.value
    });
  }

  toggleShowPassword = () => {
    this.setState({
      showPassword: !this.state.showPassword
    });
  };

  isFormValid = () => {
    const { username, password, recaptcha, loading } = this.state;
    
    // Check if username and password are filled
    const fieldsValid = username.trim() !== "" && password.trim() !== "";
    
    // In test environment, only check fields and loading state
    if (process.env.REACT_APP_TEST_ENVIRONMENT === "true") {
      return fieldsValid && !loading;
    }
    
    // In production, also check recaptcha
    return fieldsValid && recaptcha && !loading;
  };

  resetRecaptcha = () => {
    if (this.recaptcha && process.env.REACT_APP_TEST_ENVIRONMENT !== "true") {
      this.recaptcha.reset();
      this.setState({
        recaptcha: null
      });
    }
  };

  handleLogin(e) {
    e.preventDefault();

    this.setState({
      message: "",
      loading: true
    });

    this.form.validateAll();

    if (process.env.REACT_APP_TEST_ENVIRONMENT === "true") {
      if (this.checkBtn.context._errors.length === 0) {
        AuthService.login(this.state.username, this.state.password).then(
          () => {
            this.props.router.navigate("/home");
            window.location.reload();
          },
          error => {
            const resMessage =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              error.message ||
              error.toString();
  
            this.setState({
              loading: false,
              message: resMessage
            });
            this.resetRecaptcha();
          }
        );
      } else {
        this.setState({
          loading: false
        });
      }
      return;
    }

    if (!this.state.recaptcha) {
      this.setState({
        message: this.props.t("verifyHuman"),
        successful: false,
        loading: false
      });
      return;
    } else if (this.state.recaptcha === null || this.state.recaptcha === "") {
      this.setState({
        message: this.props.t("completeRecaptcha"),
        successful: false,
        loading: false
      });
      return;
    } else {
      // call recaptcha verification service
      AuthService.verifyRecaptcha(this.state.recaptcha)
        .then(response => {
          if (response.data.message === "Recaptcha verified successfully.") {
            if (this.checkBtn.context._errors.length === 0) {
              AuthService.login(this.state.username, this.state.password).then(
                () => {
                  this.props.router.navigate("/home");
                  window.location.reload();
                },
                error => {
                  const resMessage =
                    (error.response &&
                      error.response.data &&
                      error.response.data.message) ||
                    error.message ||
                    error.toString();
        
                  this.setState({
                    loading: false,
                    message: resMessage
                  });
                  this.resetRecaptcha();
                }
              );
            } else {
              this.setState({
                loading: false
              });
            }
          } else {
            this.setState({
              message: this.props.t("recaptchaFail"),
              successful: false,
              loading: false
            });
            this.resetRecaptcha();
            return;
          }
        })
        .catch(error => {
          this.setState({
            message: this.props.t("recaptchaFail"),
            successful: false,
            loading: false
          });
          this.resetRecaptcha();
        });
    }
  }

  handleForgotPassword(e) {
    e.preventDefault();
    this.openForgotPasswordModal();
  }

  openForgotPasswordModal() {
    this.setState({
      showForgotPasswordModal: true,
      resetEmail: "",
      resetMessage: "",
      resetSuccessful: false
    });
  }

  closeForgotPasswordModal() {
    this.setState({
      showForgotPasswordModal: false,
      resetEmail: "",
      resetMessage: "",
      resetSuccessful: false,
      resetLoading: false
    });
  }

  onChangeResetEmail(e) {
    this.setState({
      resetEmail: e.target.value
    });
  }

  sendPasswordReset(e) {
    e.preventDefault();
    
    const { t } = this.props;
    
    // Check if email is provided
    if (!this.state.resetEmail.trim()) {
      this.setState({
        resetMessage: t("enterEmail"),
        resetSuccessful: false
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.state.resetEmail)) {
      this.setState({
        resetMessage: t("enterValidEmail"),
        resetSuccessful: false
      });
      return;
    }

    this.setState({
      resetLoading: true,
      resetMessage: ""
    });

    // Send password reset email
    EmailService.sendPasswordResetEmail({ 
      email: this.state.resetEmail
    })
    .then(response => {
      this.setState({
        resetMessage: t("passwordResetEmailSent"),
        resetSuccessful: true,
        resetLoading: false
      });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        this.closeForgotPasswordModal();
      }, 2000);
    })
    .catch(error => {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      this.setState({
        resetLoading: false,
        resetMessage: resMessage,
        resetSuccessful: false
      });
    });
  }

  render() {
    const { t } = this.props;
    return (
      <div className="col-md-12">
        <div className="card card-container">
          <img
            src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
            alt="profile-img"
            className="profile-img-card"
          />

          <Form
            onSubmit={this.handleLogin}
            ref={c => {
              this.form = c;
            }}
          >
            <div className="form-group">
              <label htmlFor="username">{t("username")}</label>
              <Input
                id="username-login"
                type="text"
                className="form-control"
                name="username"
                value={this.state.username}
                onChange={this.onChangeUsername}
                validations={[required(t), vsafe(t)]}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t("password")}</label>
              <div className="input-group">
                <Input
                  id="password-login"
                  type={this.state.showPassword ? "text" : "password"}
                  className="form-control"
                  name="password"
                  value={this.state.password}
                  onChange={this.onChangePassword}
                  validations={[required(t), vsafe(t)]}
                />
                <div className="input-group-append">
                  <span className="input-group-text" onClick={this.toggleShowPassword} style={{ cursor: 'pointer' }}>
                    {this.state.showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <button
                id="login-button"
                className="btn btn-primary btn-block"
                disabled={!this.isFormValid()}
              >
                {this.state.loading && (
                  <span className="spinner-border spinner-border-sm"></span>
                )}
                <span>{t("login")}</span>
              </button>
            </div>

            {process.env.REACT_APP_TEST_ENVIRONMENT !== "true" && (
              <div className="form-group">
                <div className="d-flex justify-content-center">
                  <ReCaptcha
                    id="recaptcha-login"
                    ref={c => {
                      this.recaptcha = c;
                    }}
                    sitekey={process.env.REACT_APP_SITE_KEY}
                    size="compact"
                    onChange={value => {
                      this.setState({
                        recaptcha: value
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {this.state.message && (
              <div className="form-group">
                <div className="alert alert-danger" role="alert">
                  {this.state.message}
                </div>
              </div>
            )}
            <CheckButton
              style={{ display: "none" }}
              ref={(c) => {
                this.checkBtn = c;
              }}
            />
          </Form>
          
          <div className="text-center mt-3">
            <a 
              href="#" 
              onClick={this.handleForgotPassword}
              className="text-muted"
              style={{ textDecoration: 'none', fontSize: '0.9rem' }}
            >
              {t("forgotPassword")}
            </a>
          </div>
          
          <Link id="signup-link" to={"/register"} className="nav-link">
            {t("noAccount")}
          </Link>
        </div>

        {/* Forgot Password Modal */}
        {this.state.showForgotPasswordModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("forgotPassword")}</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={this.closeForgotPasswordModal}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">{t("enterEmailForPasswordReset")}</p>
                  <form onSubmit={this.sendPasswordReset}>
                    <div className="form-group mb-3">
                      <label htmlFor="resetEmail">{t("email")}</label>
                      <input
                        id="resetEmail"
                        type="email"
                        className="form-control"
                        value={this.state.resetEmail}
                        onChange={this.onChangeResetEmail}
                        placeholder={t("enterEmail")}
                        required
                      />
                    </div>
                    
                    {this.state.resetMessage && (
                      <div className={`alert ${this.state.resetSuccessful ? 'alert-success' : 'alert-danger'}`} role="alert">
                        {this.state.resetMessage}
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-end gap-2">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={this.closeForgotPasswordModal}
                      >
                        {t("cancel")}
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={this.state.resetLoading || !this.state.resetEmail.trim()}
                      >
                        {this.state.resetLoading && (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        )}
                        {t("sendResetEmail")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(withRouter(Login));